import React from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { cn } from '../../../lib/utils';
import { Pencil, Plus, User, Users, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { toast } from 'sonner';
// Reparte 100% en partes iguales entre N propietarios, manteniendo 2 decimales
// y poniendo el resto en el último para que sume exacto 100.
const distributePercentages = (count) => {
  if (count <= 0) return [];
  if (count === 1) return [100];
  const base = Math.floor((100 / count) * 100) / 100; // p.ej. 33.33
  const arr = new Array(count - 1).fill(base);
  const last = Math.round((100 - base * (count - 1)) * 100) / 100; // p.ej. 33.34
  arr.push(last);
  return arr;
};

const rebalance = (propsList) => {
  const pcts = distributePercentages(propsList.length);
  return propsList.map((p, i) => ({ ...p, porcentaje: pcts[i] }));
};

export const PropietarioDialog = ({ cn, editingPropietario, handleSavePropietario, isPropietarioDialogOpen, propietarioForm, propietarioSearch, propietarios, resultados, saving, selectedUnidad, setEditingPropietario, setIsNuevoPropietarioOpen, setIsPropietarioDialogOpen, setPropietarioForm, setPropietarioSearch }) => {
  // Wrapper que valida porcentajes antes de llamar al save real del padre
  const handleSubmitValidated = (e) => {
    const props = propietarioForm.propietarios || [];
    if (props.length > 0) {
      // 1) Duplicados
      const ids = props.map(p => p.propietario_id);
      if (new Set(ids).size !== ids.length) {
        e.preventDefault();
        toast.error('Hay propietarios duplicados.');
        return;
      }
      // 2) Rango de cada porcentaje (0..100, sin NaN ni negativos)
      const outOfRange = props.some(p => {
        const v = parseFloat(p.porcentaje);
        return isNaN(v) || v < 0 || v > 100;
      });
      if (outOfRange) {
        e.preventDefault();
        toast.error('Los porcentajes deben estar entre 0 y 100.');
        return;
      }
      // 3) Suma = 100 (handleSavePropietario también valida, pero lo cortamos antes)
      const sum = props.reduce((acc, p) => acc + (parseFloat(p.porcentaje) || 0), 0);
      if (Math.abs(sum - 100) > 0.01) {
        e.preventDefault();
        toast.error(`Los porcentajes deben sumar 100% (actual: ${sum.toFixed(2)}%).`);
        return;
      }
    }
    // Limpiar buscador al confirmar
    setPropietarioSearch('');
    handleSavePropietario(e);
  };
  return (
      <Dialog open={isPropietarioDialogOpen} onOpenChange={(open) => { setIsPropietarioDialogOpen(open); if (!open) { setPropietarioSearch(''); setEditingPropietario(false); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="propietario-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Propietario(s)
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitValidated} className="space-y-4">
            <p className="text-sm text-slate-500">
              Unidad: <strong>{selectedUnidad?.nombre_unidad}</strong>
            </p>
            
            {/* Propietario(s) actual(es) - Solo mostrar si hay y no está en modo edición */}
            {propietarioForm.propietarios && propietarioForm.propietarios.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Propietario(s) asignado(s)</Label>
                  {!editingPropietario && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingPropietario(true)}
                    >
                      <Pencil className="w-3 h-3 mr-1" />
                      Cambiar
                    </Button>
                  )}
                </div>
                <div className="border rounded-md divide-y">
                  {propietarioForm.propietarios.map((p, idx) => {
                    const propInfo = propietarios.find(pr => pr.propietario_id === p.propietario_id);
                    return (
                      <div key={idx} className="flex items-center justify-between p-3 bg-blue-50">
                        <div className="flex-1">
                          <span className="font-medium">{propInfo?.nombre_razon_social || 'Propietario no encontrado'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            className="w-20 h-8 text-sm"
                            value={p.porcentaje}
                            onChange={(e) => {
                              const newPropietarios = [...propietarioForm.propietarios];
                              newPropietarios[idx].porcentaje = parseFloat(e.target.value) || 0;
                              setPropietarioForm({...propietarioForm, propietarios: newPropietarios});
                            }}
                          />
                          <span className="text-sm text-slate-500">%</span>
                          {/* Quitar propietario (incluso el último → deja la unidad sin propietario) */}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                            title={propietarioForm.propietarios.length > 1 ? 'Quitar propietario' : 'Desvincular propietario (deja la unidad sin propietario asignado)'}
                            data-testid={`btn-quitar-propietario-${idx}`}
                            onClick={() => {
                              if (propietarioForm.propietarios.length === 1) {
                                if (!window.confirm('¿Desvincular este propietario? La unidad quedará sin propietario asignado.\n\nNo se borra el propietario del sistema, solo la relación con esta unidad.')) return;
                                setPropietarioForm({ ...propietarioForm, propietarios: [], propietario_id: '' });
                                return;
                              }
                              const newPropietarios = propietarioForm.propietarios.filter((_, i) => i !== idx);
                              const rebalanced = rebalance(newPropietarios);
                              setPropietarioForm({...propietarioForm, propietarios: rebalanced, propietario_id: rebalanced[0]?.propietario_id || ''});
                            }}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Mostrar suma de porcentajes si hay más de uno */}
                {propietarioForm.propietarios.length > 1 && (
                  <div className={cn(
                    "text-sm text-right",
                    Math.abs(propietarioForm.propietarios.reduce((sum, p) => sum + (parseFloat(p.porcentaje) || 0), 0) - 100) < 0.01 
                      ? "text-green-600" 
                      : "text-red-600"
                  )}>
                    Total: {propietarioForm.propietarios.reduce((sum, p) => sum + (parseFloat(p.porcentaje) || 0), 0).toFixed(2)}%
                    {Math.abs(propietarioForm.propietarios.reduce((sum, p) => sum + (parseFloat(p.porcentaje) || 0), 0) - 100) > 0.01 && (
                      <span className="ml-2">(debe ser 100%)</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Buscador de propietarios - Solo mostrar si está en modo edición o no hay propietarios */}
            {(editingPropietario || propietarioForm.propietarios?.length === 0) && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>{propietarioForm.propietarios?.length > 0 ? 'Agregar otro propietario' : 'Seleccionar propietario'}</Label>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    className="text-blue-600 h-auto py-1"
                    onClick={() => setIsNuevoPropietarioOpen(true)}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Nuevo Propietario
                  </Button>
                </div>
                {/* Buscador y lista */}
                <div className="space-y-2">
                  <Input 
                    placeholder="Buscar propietario..." 
                    value={propietarioSearch}
                    onChange={(e) => setPropietarioSearch(e.target.value)}
                  />
                  <div className="border rounded-md max-h-40 overflow-y-auto">
                    {propietarioForm.propietarios?.length === 0 && (
                      <div 
                        className={cn(
                          "px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm",
                          (!propietarioForm.propietario_id || propietarioForm.propietario_id === '') && "bg-slate-100 font-medium"
                        )}
                        onClick={() => setPropietarioForm({...propietarioForm, propietario_id: '', propietarios: []})}
                      >
                        Sin propietario
                      </div>
                    )}
                    {propietarios
                      .filter(p => p.nombre_razon_social.toLowerCase().includes(propietarioSearch.toLowerCase()))
                      .filter(p => !propietarioForm.propietarios?.some(pp => pp.propietario_id === p.propietario_id))
                      .sort((a, b) => a.nombre_razon_social.localeCompare(b.nombre_razon_social))
                      .map((p) => (
                        <div 
                          key={p.propietario_id}
                          className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm flex items-center justify-between"
                          onClick={() => {
                            if (propietarioForm.propietarios?.length > 0) {
                              // Agregar a la lista existente y rebalancear porcentajes
                              const newPropietarios = [...propietarioForm.propietarios, {
                                propietario_id: p.propietario_id,
                                porcentaje: 0,
                              }];
                              const rebalanced = rebalance(newPropietarios);
                              setPropietarioForm({...propietarioForm, propietarios: rebalanced});
                            } else {
                              // Primer propietario - 100%
                              setPropietarioForm({
                                ...propietarioForm, 
                                propietario_id: p.propietario_id, 
                                propietarios: [{
                                  propietario_id: p.propietario_id,
                                  porcentaje: 100,
                                }]
                              });
                              setEditingPropietario(false);
                            }
                            // Limpiar buscador al seleccionar
                            setPropietarioSearch('');
                          }}
                        >
                          <span>{p.nombre_razon_social}</span>
                          <Plus className="w-4 h-4 text-blue-600" />
                        </div>
                      ))}
                    {propietarios.filter(p => p.nombre_razon_social.toLowerCase().includes(propietarioSearch.toLowerCase())).filter(p => !propietarioForm.propietarios?.some(pp => pp.propietario_id === p.propietario_id)).length === 0 && (
                      <div className="px-3 py-2 text-sm text-slate-500 italic">
                        No se encontraron resultados
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Botón para cancelar edición si ya había propietarios */}
                {propietarioForm.propietarios?.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full text-slate-500"
                    onClick={() => setEditingPropietario(false)}
                  >
                    Cancelar cambio
                  </Button>
                )}
              </div>
            )}
            
            {/* Botón para agregar copropiedad si tiene un solo propietario */}
            {!editingPropietario && propietarioForm.propietarios?.length === 1 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setEditingPropietario(true)}
              >
                <Users className="w-4 h-4 mr-2" />
                Agregar otro propietario (copropiedad)
              </Button>
            )}
            
            <div className="space-y-2">
              <Label>Fecha de Adquisición</Label>
              <Input 
                type="date" 
                value={propietarioForm.fecha_adquisicion} 
                onChange={(e) => setPropietarioForm({...propietarioForm, fecha_adquisicion: e.target.value})} 
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-1.5 col-span-2 md:col-span-1">
                <Label className="text-xs">Monto Neto</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={propietarioForm.monto_inversion?.monto ?? ''}
                  onChange={(e) => setPropietarioForm({
                    ...propietarioForm,
                    monto_inversion: {
                      ...(propietarioForm.monto_inversion || { moneda: 'USD', iva: 0 }),
                      monto: e.target.value,
                    },
                  })}
                  placeholder="0"
                  data-testid="input-monto-neto"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">IVA</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={propietarioForm.monto_inversion?.iva ?? 0}
                  onChange={(e) => setPropietarioForm({
                    ...propietarioForm,
                    monto_inversion: {
                      ...(propietarioForm.monto_inversion || { monto: '', moneda: 'USD' }),
                      iva: e.target.value,
                    },
                  })}
                  placeholder="0"
                  data-testid="input-iva-compra"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Moneda</Label>
                <Select
                  value={propietarioForm.monto_inversion?.moneda || 'USD'}
                  onValueChange={(v) => setPropietarioForm({
                    ...propietarioForm,
                    monto_inversion: {
                      ...(propietarioForm.monto_inversion || { monto: '', iva: 0 }),
                      moneda: v,
                    },
                  })}
                >
                  <SelectTrigger data-testid="select-moneda-compra"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="ARS">ARS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 col-span-2 md:col-span-1">
                <Label className="text-xs text-slate-500">Total Compra</Label>
                <div
                  className="min-h-9 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm font-mono text-slate-700 truncate"
                  data-testid="total-compra"
                  title={(() => {
                    const neto = Number(propietarioForm.monto_inversion?.monto || 0);
                    const iva = Number(propietarioForm.monto_inversion?.iva || 0);
                    const total = neto + iva;
                    const moneda = propietarioForm.monto_inversion?.moneda || 'USD';
                    return total > 0
                      ? `${moneda} ${total.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : '';
                  })()}
                >
                  {(() => {
                    const neto = Number(propietarioForm.monto_inversion?.monto || 0);
                    const iva = Number(propietarioForm.monto_inversion?.iva || 0);
                    const total = neto + iva;
                    const moneda = propietarioForm.monto_inversion?.moneda || 'USD';
                    return total > 0
                      ? `${moneda} ${total.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : '—';
                  })()}
                </div>
              </div>
            </div>
            <DialogFooter className="sticky bottom-0 bg-white pt-2 -mx-6 px-6 -mb-6 pb-4 border-t">
              <Button type="button" variant="outline" onClick={() => { setPropietarioSearch(''); setIsPropietarioDialogOpen(false); }} data-testid="propietario-cancel-btn">Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="propietario-save-btn">{saving ? 'Guardando...' : 'Guardar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
  );
};
